import React, {
  useRef,
  useEffect,
  ChangeEvent,
  DragEvent,
  useState,
} from "react";
import {
  Card,
  CardHeader,
  Divider,
  CardContent,
  Grid,
  Box,
  IconButton,
  Typography,
  Button,
  Link,
  CircularProgress,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import UploadIcon from "../../assets/icons/upload.svg";
import InfoIcon from "../../assets/icons/information.svg";
import ReplayIcon from "@mui/icons-material/Replay";
import DeleteOutlineIcon from "../../assets/icons/trash.svg";
import { FileList } from "../../types/guestOrder";
import { useLocation } from "react-router-dom";
import { allowedTypes, allowedExt } from "../../utils/common/constants";
import { showSnackbar } from "../../store/snackbar/slice";
import { useDispatch, useSelector } from "react-redux";
import { analyzeImageRequest } from "../../store/imageAnalyze/slice";
import { getAnalysisMessage } from "../../utils/common/helper";
import { RootState } from "../../store/root/config.store";
import { addFiles, removeFile } from "../../store/guestOrderUploadFile/slice";
import { analyzeUploadedFiles } from "../../utils/common/analyseUtils";
import {
  buildExistingBuffers,
  getCleanedStateFiles,
  getUsedIndexesAndNextIndex,
  readFileAsArrayBuffer,
  buffersAreEqual,
} from "../../utils/uploadFileUtils";
import { useNullableFieldPrivateContext } from "@mui/x-date-pickers/internals";

interface UploadFileProps {
  onAddMore?: () => void;
}

const GuestUploadFile: React.FC<UploadFileProps> = (props) => {
  const reduxDispatch = useDispatch();
  const fileListContainerRef = useRef<HTMLDivElement | null>(null);
  const { control, setValue, watch } = useFormContext();

  const dispatch = useDispatch();
  const uploadedFiles = useSelector(
    (state: RootState) => state?.guestUploadFiles?.files
  );
  const uploadError = useSelector(
    (state: RootState) => state?.guestUploadFiles?.error
  );

  const location = useLocation();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const labelName = location.state?.labelName || "";

  useEffect(() => {
    const state = location.state;
    const isFromLabelCatalog = state?.fromLabelCatalog === true;

    if (!isFromLabelCatalog) {
      dispatch(addFiles([]));
      setValue("OrderAttachments", []);
    }
  }, []);

  useEffect(() => {
    const current = watch("OrderAttachments");
    if (JSON.stringify(current) !== JSON.stringify(uploadedFiles)) {
      setValue("OrderAttachments", uploadedFiles);
    }
  }, [uploadedFiles]);

  useEffect(() => {
    const formFiles = watch("OrderAttachments");
    if (
      Array.isArray(formFiles) &&
      JSON.stringify(formFiles) !== JSON.stringify(uploadedFiles)
    ) {
      dispatch(addFiles(formFiles as FileList[]));
    }
  }, [watch("OrderAttachments")]);

  useEffect(() => {
    if (fileListContainerRef.current) {
      fileListContainerRef.current.scrollTop =
        fileListContainerRef.current.scrollHeight;
    }
  }, [uploadedFiles]);

  const handleFileInput = async (files: File[]) => {
    const skipped: string[] = [];
    const validFiles: FileList[] = [];

    const cleanedStateFiles = getCleanedStateFiles(uploadedFiles, files);

    const currentActiveCount = cleanedStateFiles.filter(
      (file) => file.isActive
    ).length;

    const existingBuffers = await buildExistingBuffers(cleanedStateFiles);

    let fileIndex = 1;
    const usedIndexes = new Set<number>();

    for (const file of files) {
      if (validFiles.length + currentActiveCount >= 4) {
        reduxDispatch(
          showSnackbar({
            message: "You can upload a maximum of 4 files.",
            severity: "warning",
          })
        );
        break;
      }

      const ext = file.name.split(".").pop()?.toLowerCase() || "";
      const isTypeAllowed = allowedTypes.includes(file.type);
      const isExtAllowed = allowedExt.includes(ext);
      const megabytes = file.size / (1024 * 1024);

       if (megabytes > 2) {
        skipped.push(`${file.name} (Maximum allowed file size of 2MB)`);
        continue;
      }

      if (!isTypeAllowed && !isExtAllowed) {
        skipped.push(`${file.name} (unsupported type)`);
        continue;
      }

      const fileBuffer = await readFileAsArrayBuffer(file);

      const isDuplicate = existingBuffers.some(
        (existing) =>
          existing.size === file.size &&
          buffersAreEqual(existing.buffer, fileBuffer)
      );

      if (isDuplicate) {
        skipped.push(`${file.name} (duplicate)`);
        continue;
      }

      let fileName = file.name;
      let attachment = file;

      validFiles.push({
        fileName,
        orderAttachmentID: 0,
        attachment,
        isEmailContent: false,
        isActive: true
      });

      existingBuffers.push({
        name: file.name,
        size: file.size,
        buffer: fileBuffer,
      });
    }

    if (skipped.length > 0) {
      debugger;
      reduxDispatch(
        showSnackbar({
          message: `Skipped ${skipped.length} file(s): ${skipped.join(", ")}`,
          severity: "warning",
        })
      );
    }

    if (validFiles.length > 0) {
      dispatch(addFiles(validFiles));
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const droppedFiles = Array.from(event.dataTransfer.files);
    handleFileInput(droppedFiles);
  };

  const handleBoxClick = () => {
    inputRef.current?.click();
  };

  const handleDelete = (fileToDelete: FileList) => {
    dispatch(removeFile(fileToDelete));
  };

  return (
    <Card
      className="upload-file-card"
      sx={{ padding: 1, boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)" }}
    >
      <CardHeader
        className="card-header"
        title="Upload Label*"
        sx={{
          padding: "8px 16px",
          "& .MuiTypography-root": { fontSize: "16px" },
        }}
      />
      <Divider />
      <CardContent
        className="upload-card-content"
        sx={{ padding: "12px 16px" }}
      >
        <Grid container spacing={1}>
          {/* Upload Area */}
          <Grid size={{ xs: 12, sm: 12, md: 5, lg: 5 }}>
            <Box display="flex" flexDirection="column" gap={1}>
              <Box
                sx={{
                  border: uploadError
                    ? "2px dashed #d32f2f"
                    : "2px dashed #005596",
                  borderRadius: 1,
                  padding: 1,
                  textAlign: "center",
                  cursor: "pointer",
                  minHeight: 200,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={handleBoxClick}
              >
                <input
                  ref={inputRef}
                  type="file"
                  hidden
                  multiple
                  accept=".jpg,.jpeg,.png,.pdf,.webp,.svg"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    handleFileInput(Array.from(e.target.files || []));
                    e.target.value = "";
                  }}
                />
                <IconButton>
                  <img
                    src={UploadIcon}
                    alt="Upload"
                    style={{ width: 32, height: 32 }}
                  />
                </IconButton>
                <Typography variant="body2" color="textSecondary">
                  Drag your file(s) to start uploading
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  component="span"
                  sx={{
                    marginTop: 1,
                    borderRadius: "30px",
                    fontSize: "0.7rem",
                  }}
                >
                  Browse files
                </Button>
              </Box>
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
              >
                <Typography variant="caption" color="textSecondary">
                  Upload file format: ".jpg, .jpeg, .png, .pdf, .webp,
                  .svg"
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Max files:4, Max file size:2MB
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <img
                  src={InfoIcon}
                  alt="info"
                  style={{ width: 16, height: 16 }}
                />
                <Typography variant="caption" color="textSecondary">
                  Review pictures for clarity
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* File List */}
          <Grid size={{ xs: 12, md: 7, lg: 7 }}>
            <Controller
              name="OrderAttachments"
              control={control}
              defaultValue={[]}
              render={({ field }) => (
                <Box
                  ref={fileListContainerRef}
                  sx={{ maxHeight: 200, overflowY: "auto", paddingRight: 1 }}
                >
                   {field.value.length === 0 ? (
                    <div className="nofilestext"  style={{"textAlign":"center"}}>
                      <Typography variant="caption" color="textSecondary">
                        No files uploaded yet.
                      </Typography>
                    </div>
                  ) : (
                    <Grid container spacing={1} direction="column">
                      {field.value
                        .filter((file: any) => file.isActive)
                        .map((file: any, index: number) => {
                          return (
                            <Grid
                              key={`${file.fileName}_${file.attachment.size}_${file.attachment.lastModified}`}
                            >
                              <Box
                                sx={{
                                  border: "1px solid #E7E7E7",
                                  borderRadius: 1,
                                  padding: 1,
                                  backgroundColor: "#FFFFFF",
                                }}
                              >
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <Link
                                    href={
                                      file.attachment instanceof File ||
                                      file.attachment instanceof Blob
                                        ? URL.createObjectURL(file.attachment)
                                        : undefined
                                    }
                                    target="_blank"
                                    underline="hover"
                                    sx={{
                                      fontSize: "0.85rem",
                                    }}
                                  >
                                    {file.fileName}
                                  </Link>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDelete(file)}
                                  >
                                    <img
                                      src={DeleteOutlineIcon}
                                      alt="delete-icon"
                                    />
                                  </IconButton>
                                </Box>
                              </Box>
                            </Grid>
                          );
                        })}
                    </Grid>
                  )}
                </Box>
              )}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default GuestUploadFile;
